import {
  aircraftReviewSettingsStorageKey,
  chtCoolingRateTriggerFpm,
  chtCoolingRateTriggerMaxFpm,
  chtCoolingRateTriggerMinFpm,
  hiddenFlightsStorageKey,
  selectedAircraftWorkspaceStorageKey,
  sensitivityModeOrder,
  sensitivityStorageKey,
  selectedFlightStorageKey,
  seriesPaneOverridesStorageKey,
  trendExcludedFlightsStorageKey,
  trendIncludedFlightsStorageKey,
  themePreferenceStorageKey,
  themePreferenceOrder,
  defaultThemePreference,
  aircraftOrderStorageKey,
  aircraftOrderOptions,
  defaultAircraftOrder,
  flightOrderStorageKey,
  flightOrderOptions,
  defaultFlightOrder,
  aircraftArtViewStorageKey,
  aircraftArtViewOptions,
  defaultAircraftArtView,
} from "./config.js";

export function loadHiddenFlightIds(storage = globalThis.localStorage) {
  try {
    const stored = JSON.parse(storage.getItem(hiddenFlightsStorageKey) ?? "[]");
    return new Set(Array.isArray(stored) ? stored : []);
  } catch {
    return new Set();
  }
}

export function saveHiddenFlightIds(hiddenFlightIds, storage = globalThis.localStorage) {
  storage.setItem(hiddenFlightsStorageKey, JSON.stringify([...hiddenFlightIds]));
}

export function loadTrendExcludedFlightIds(storage = globalThis.localStorage) {
  try {
    const stored = JSON.parse(storage.getItem(trendExcludedFlightsStorageKey) ?? "[]");
    return new Set(Array.isArray(stored) ? stored : []);
  } catch {
    return new Set();
  }
}

export function saveTrendExcludedFlightIds(excludedFlightIds, storage = globalThis.localStorage) {
  storage.setItem(trendExcludedFlightsStorageKey, JSON.stringify([...excludedFlightIds]));
}

export function loadTrendIncludedFlightIds(storage = globalThis.localStorage) {
  try {
    const stored = JSON.parse(storage.getItem(trendIncludedFlightsStorageKey) ?? "[]");
    return new Set(Array.isArray(stored) ? stored : []);
  } catch {
    return new Set();
  }
}

export function saveTrendIncludedFlightIds(includedFlightIds, storage = globalThis.localStorage) {
  storage.setItem(trendIncludedFlightsStorageKey, JSON.stringify([...includedFlightIds]));
}

export function normalizeSensitivity(value) {
  return sensitivityModeOrder.includes(value) ? value : "balanced";
}

export function loadDetectionSensitivity(storage = globalThis.localStorage) {
  try {
    return normalizeSensitivity(storage.getItem(sensitivityStorageKey));
  } catch {
    return "balanced";
  }
}

export function saveDetectionSensitivity(detectionSensitivity, storage = globalThis.localStorage) {
  storage.setItem(sensitivityStorageKey, detectionSensitivity);
}

export function normalizeSelectedAircraftWorkspaceId(value) {
  return String(value ?? "").trim();
}

export function loadSelectedAircraftWorkspaceId(storage = globalThis.localStorage) {
  try {
    return normalizeSelectedAircraftWorkspaceId(storage.getItem(selectedAircraftWorkspaceStorageKey));
  } catch {
    return "";
  }
}

export function saveSelectedAircraftWorkspaceId(aircraft, storage = globalThis.localStorage) {
  storage.setItem(selectedAircraftWorkspaceStorageKey, normalizeSelectedAircraftWorkspaceId(aircraft));
}

export function normalizeSelectedFlightId(value) {
  return String(value ?? "").trim();
}

export function loadSelectedFlightId(storage = globalThis.localStorage) {
  try {
    return normalizeSelectedFlightId(storage.getItem(selectedFlightStorageKey));
  } catch {
    return "";
  }
}

export function saveSelectedFlightId(flightId, storage = globalThis.localStorage) {
  storage.setItem(selectedFlightStorageKey, normalizeSelectedFlightId(flightId));
}

function normalizedReviewMonitorKey(value) {
  const key = String(value ?? "").trim();
  return /^[A-Za-z][A-Za-z0-9]*$/.test(key) ? key : "";
}

function normalizedAircraftKey(value) {
  return String(value ?? "").trim();
}

export function normalizeCoolingRateTriggerFpm(value, fallback = chtCoolingRateTriggerFpm) {
  const fallbackNumber = Number.isFinite(Number(fallback)) ? Number(fallback) : chtCoolingRateTriggerFpm;
  const normalizedFallback = Math.min(
    chtCoolingRateTriggerMaxFpm,
    Math.max(chtCoolingRateTriggerMinFpm, Math.round(fallbackNumber)),
  );
  if (value === null || value === undefined || String(value).trim() === "") return normalizedFallback;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return normalizedFallback;
  return Math.min(
    chtCoolingRateTriggerMaxFpm,
    Math.max(chtCoolingRateTriggerMinFpm, Math.round(numeric)),
  );
}

export function normalizeAircraftReviewSettings(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value)
    .map(([aircraft, settings]) => {
      const aircraftKey = normalizedAircraftKey(aircraft);
      if (!aircraftKey || !settings || typeof settings !== "object" || Array.isArray(settings)) return null;
      const settingValue = settings as { disabledMonitorKeys?: unknown; coolingRateTriggerFpm?: unknown };
      const disabledMonitorKeys = [...new Set((Array.isArray(settingValue.disabledMonitorKeys) ? settingValue.disabledMonitorKeys : [])
        .map(normalizedReviewMonitorKey)
        .filter(Boolean))]
        .sort();
      return [aircraftKey, {
        disabledMonitorKeys,
        coolingRateTriggerFpm: normalizeCoolingRateTriggerFpm(settingValue.coolingRateTriggerFpm),
      }];
    })
    .filter(Boolean));
}

export function loadAircraftReviewSettings(storage = globalThis.localStorage) {
  try {
    return normalizeAircraftReviewSettings(JSON.parse(storage.getItem(aircraftReviewSettingsStorageKey) ?? "{}"));
  } catch {
    return {};
  }
}

export function saveAircraftReviewSettings(settings, storage = globalThis.localStorage) {
  storage.setItem(aircraftReviewSettingsStorageKey, JSON.stringify(normalizeAircraftReviewSettings(settings)));
}

export function disabledReviewMonitorKeysForAircraft(settings, aircraft) {
  const aircraftKey = normalizedAircraftKey(aircraft);
  if (!aircraftKey) return new Set();
  return new Set(normalizeAircraftReviewSettings(settings)[aircraftKey]?.disabledMonitorKeys ?? []);
}

export function reviewMonitorIsEnabled(settings, aircraft, key) {
  const monitorKey = normalizedReviewMonitorKey(key);
  if (!monitorKey) return true;
  return !disabledReviewMonitorKeysForAircraft(settings, aircraft).has(monitorKey);
}

export function coolingRateTriggerFpmForAircraft(settings, aircraft) {
  const aircraftKey = normalizedAircraftKey(aircraft);
  if (!aircraftKey) return chtCoolingRateTriggerFpm;
  return normalizeAircraftReviewSettings(settings)[aircraftKey]?.coolingRateTriggerFpm ?? chtCoolingRateTriggerFpm;
}

const seriesPaneNames = ["primary", "secondary"];

function normalizedSeriesKey(value) {
  const key = String(value ?? "").trim();
  return /^[A-Za-z][A-Za-z0-9]*$/.test(key) ? key : "";
}

export function normalizeSeriesPaneOverrides(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value)
    .map(([aircraft, overrides]) => {
      const aircraftKey = normalizedAircraftKey(aircraft);
      if (!aircraftKey || !overrides || typeof overrides !== "object" || Array.isArray(overrides)) return null;
      const entries = Object.entries(overrides)
        .map(([seriesKey, pane]) => [normalizedSeriesKey(seriesKey), pane])
        .filter(([seriesKey, pane]) => seriesKey && seriesPaneNames.includes(pane))
        .sort(([a], [b]) => a.localeCompare(b));
      if (!entries.length) return null;
      return [aircraftKey, Object.fromEntries(entries)];
    })
    .filter(Boolean));
}

export function loadSeriesPaneOverrides(storage = globalThis.localStorage) {
  try {
    return normalizeSeriesPaneOverrides(JSON.parse(storage.getItem(seriesPaneOverridesStorageKey) ?? "{}"));
  } catch {
    return {};
  }
}

export function saveSeriesPaneOverrides(overrides, storage = globalThis.localStorage) {
  storage.setItem(seriesPaneOverridesStorageKey, JSON.stringify(normalizeSeriesPaneOverrides(overrides)));
}

export function seriesPaneOverridesForAircraft(overrides, aircraft) {
  const aircraftKey = normalizedAircraftKey(aircraft);
  if (!aircraftKey) return {};
  return normalizeSeriesPaneOverrides(overrides)[aircraftKey] ?? {};
}

// pane === null clears the override for that series; an aircraft entry with no
// remaining overrides is dropped entirely so storage never accumulates husks.
export function updateAircraftSeriesPaneOverride(overrides, aircraft, seriesKey, pane) {
  const normalizedOverrides = normalizeSeriesPaneOverrides(overrides);
  const aircraftKey = normalizedAircraftKey(aircraft);
  const series = normalizedSeriesKey(seriesKey);
  if (!aircraftKey || !series) return normalizedOverrides;
  const aircraftOverrides = { ...(normalizedOverrides[aircraftKey] ?? {}) };
  if (pane === null || pane === undefined) delete aircraftOverrides[series];
  else if (seriesPaneNames.includes(pane)) aircraftOverrides[series] = pane;
  else return normalizedOverrides;
  const next = { ...normalizedOverrides };
  if (Object.keys(aircraftOverrides).length) next[aircraftKey] = aircraftOverrides;
  else delete next[aircraftKey];
  return normalizeSeriesPaneOverrides(next);
}

export function clearAircraftSeriesPaneOverrides(overrides, aircraft) {
  const normalizedOverrides = normalizeSeriesPaneOverrides(overrides);
  const aircraftKey = normalizedAircraftKey(aircraft);
  if (!aircraftKey || !normalizedOverrides[aircraftKey]) return normalizedOverrides;
  const next = { ...normalizedOverrides };
  delete next[aircraftKey];
  return next;
}

export function updateAircraftReviewMonitorSettings(settings, aircraft, disabledMonitorKeys = [], options: { coolingRateTriggerFpm?: unknown } = {}) {
  const normalizedSettings = normalizeAircraftReviewSettings(settings);
  const aircraftKey = normalizedAircraftKey(aircraft);
  if (!aircraftKey) return normalizedSettings;
  const normalizedDisabledKeys = [...new Set(disabledMonitorKeys
    .map(normalizedReviewMonitorKey)
    .filter(Boolean))]
    .sort();
  return {
    ...normalizedSettings,
    [aircraftKey]: {
      disabledMonitorKeys: normalizedDisabledKeys,
      coolingRateTriggerFpm: normalizeCoolingRateTriggerFpm(options.coolingRateTriggerFpm),
    },
  };
}

// --- Account > Settings display preferences --------------------------------
// Same shape as everything above: normalize() is the single place that decides
// what a stored value means, so a hand-edited or stale localStorage entry can
// never put the UI into a state the rest of the code does not expect.

export function normalizeThemePreference(value) {
  return themePreferenceOrder.includes(value) ? value : defaultThemePreference;
}

export function loadThemePreference(storage = globalThis.localStorage) {
  try {
    return normalizeThemePreference(storage.getItem(themePreferenceStorageKey));
  } catch {
    return defaultThemePreference;
  }
}

export function saveThemePreference(theme, storage = globalThis.localStorage) {
  try {
    storage.setItem(themePreferenceStorageKey, normalizeThemePreference(theme));
  } catch {
    /* storage unavailable (private mode, quota): the choice just does not stick */
  }
}

/**
 * Resolve a preference to the theme actually applied. "system" consults the OS;
 * everything else is literal. `prefersDark` is injected so this stays a pure
 * function and can be tested without a matchMedia stub.
 */
export function resolveTheme(preference, prefersDark = true) {
  const normalized = normalizeThemePreference(preference);
  if (normalized !== "system") return normalized;
  return prefersDark ? "dark" : "light";
}

export function normalizeAircraftOrder(value) {
  return aircraftOrderOptions.includes(value) ? value : defaultAircraftOrder;
}

export function loadAircraftOrder(storage = globalThis.localStorage) {
  try {
    return normalizeAircraftOrder(storage.getItem(aircraftOrderStorageKey));
  } catch {
    return defaultAircraftOrder;
  }
}

export function saveAircraftOrder(order, storage = globalThis.localStorage) {
  try {
    storage.setItem(aircraftOrderStorageKey, normalizeAircraftOrder(order));
  } catch {
    /* storage unavailable: fall back to the default on next load */
  }
}

export function normalizeFlightOrder(value) {
  return flightOrderOptions.includes(value) ? value : defaultFlightOrder;
}

export function loadFlightOrder(storage = globalThis.localStorage) {
  try {
    return normalizeFlightOrder(storage.getItem(flightOrderStorageKey));
  } catch {
    return defaultFlightOrder;
  }
}

export function saveFlightOrder(order, storage = globalThis.localStorage) {
  try {
    storage.setItem(flightOrderStorageKey, normalizeFlightOrder(order));
  } catch {
    /* storage unavailable: fall back to the default on next load */
  }
}

export function normalizeAircraftArtView(value) {
  return aircraftArtViewOptions.includes(value) ? value : defaultAircraftArtView;
}

export function loadAircraftArtView(storage = globalThis.localStorage) {
  try {
    return normalizeAircraftArtView(storage.getItem(aircraftArtViewStorageKey));
  } catch {
    return defaultAircraftArtView;
  }
}

export function saveAircraftArtView(view, storage = globalThis.localStorage) {
  try {
    storage.setItem(aircraftArtViewStorageKey, normalizeAircraftArtView(view));
  } catch {
    /* storage unavailable: fall back to the default on next load */
  }
}
